import { Link } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertical, Edit, Trash2, User, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPhoneNumber } from '@repo/shared'

interface PatientCardProps {
  patient: {
    id: string
    full_name: string
    status: 'active' | 'inactive' | 'discharged' | 'deleted'
    condition_description?: string | null
    birth_date?: string | null
    family_members?: Array<{
      whatsapp_number: string | null
      is_primary_contact: boolean
      profiles: {
        full_name: string
        phone: string | null
      }
    }>
  }
  onEdit?: () => void
  onDelete?: () => void
}

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-yellow-100 text-yellow-800',
  discharged: 'bg-gray-100 text-gray-800',
  deleted: 'bg-red-100 text-red-800',
}

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  discharged: 'Alta',
  deleted: 'Excluido',
}

function getAge(birthDate: string | null | undefined): string | null {
  if (!birthDate) return null
  const birth = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return `${age} anos`
}

export function PatientCard({ patient, onEdit, onDelete }: PatientCardProps) {
  const age = getAge(patient.birth_date)
  const primaryContact = patient.family_members?.find(fm => fm.is_primary_contact) ?? patient.family_members?.[0]
  const contactPhone = primaryContact?.whatsapp_number ?? primaryContact?.profiles?.phone

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <Link
            to="/patients/$patientId"
            params={{ patientId: patient.id }}
            className="flex items-start gap-3 flex-1 min-w-0"
          >
            <div className="rounded-full bg-muted p-3">
              <User className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="font-medium truncate min-w-0">{patient.full_name}</h3>
                <Badge
                  className={cn('text-xs flex-shrink-0', statusColors[patient.status])}
                >
                  {statusLabels[patient.status]}
                </Badge>
              </div>
              {age && (
                <p className="text-sm text-muted-foreground">{age}</p>
              )}
              {patient.condition_description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {patient.condition_description}
                </p>
              )}
              {contactPhone && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{formatPhoneNumber(contactPhone)}</span>
                </div>
              )}
            </div>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/patients/$patientId" params={{ patientId: patient.id }}>
                  Ver detalhes
                </Link>
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="mr-2 h-4 w-4" />
                  Editar
                </DropdownMenuItem>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onDelete}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  )
}
